import { useEffect } from 'react'

import { api } from '@common-sense-privacy/common'

import { useCurrentOrganization } from '@/hooks/useSession'

import { useLazyGetLatestEvaluationQuery } from '@/services/organization/product/evaluation'

import log from '@/utils/log'

const { isTerminalEvaluationStatus } = api.endpoints.organization.product.evaluation.types

export const POLICY_EVALUATION_POLL_INTERVAL_MS = 10_000

export type PolicyEvaluationStatus = {
  evaluation: api.endpoints.organization.product.evaluation.types.LatestEvaluation | null,
  isLoading: boolean,
}

function usePolicyEvaluationStatus(productId?: string): PolicyEvaluationStatus {
  const { id } = useCurrentOrganization()
  const [
    triggerGetLatestEvaluation,
    {
      data,
      isLoading: isFirstFetchInFlight,
      isUninitialized,
    },
  ] = useLazyGetLatestEvaluationQuery()

  /**
   * Whether Gibson has finished, derived from the cached row rather than latched inside
   * the poll. Two things follow, and both matter:
   *
   * - the stop is not permanent. A retry's `invalidatesTags: ['ProductEvaluation']`
   *   refetches this query, and the fresh non-terminal row flips this back to false,
   *   which re-runs the effect and brings the interval back with no manual restart.
   * - the decision never reads a stale value. Deciding it from the poll's own resolved
   *   value did: while that invalidation refetch is in flight, RTK Query skips a second
   *   request for the same key and resolves the trigger with the entry it already has -
   *   the OLD, terminal row - so a just-retried evaluation stopped polling on the spot.
   */
  const isFinished = isTerminalEvaluationStatus(data?.data?.status)

  useEffect(() => {
    if (!id || !productId || isFinished) return undefined

    const poll = async () => {
      try {
        await triggerGetLatestEvaluation({
          params: {
            id,
            productId,
          },
        }).unwrap()
      }
      catch (error) {
        log('error', 'Failed to poll the latest policy evaluation.', error)
      }
    }

    const intervalId = setInterval(poll, POLICY_EVALUATION_POLL_INTERVAL_MS)
    poll()

    return () => clearInterval(intervalId)
  }, [
    id,
    isFinished,
    productId,
    triggerGetLatestEvaluation,
  ])

  return {
    // `null` mirrors the endpoint, which answers `null` - not a 404 - for a product that
    // has never been evaluated. Collapsing it to `undefined` would make "never evaluated"
    // indistinguishable from "not fetched yet" for the callers that render off this.
    // eslint-disable-next-line no-null/no-null
    evaluation: data?.data ?? null,
    isLoading: isUninitialized || isFirstFetchInFlight,
  }
}

export default usePolicyEvaluationStatus
